import type { SessionData, AppSettings, Course, ShortlistEntry } from '@gpa-scales/shared-types';
import { secureSave, secureLoad, secureRemove } from '@/utils/secureStorage';

const STORAGE_KEY_COURSES = 'gpa-scales_courses';
const STORAGE_KEY_SETTINGS = 'gpa-scales_settings';
const STORAGE_KEY_SHORTLIST = 'gpa-scales_college_shortlist';
const SCHEMA_VERSION = 1;

export async function saveToLocalStorage(
  courses: Course[],
  settings: AppSettings,
  shortlist?: ShortlistEntry[],
): Promise<void> {
  try {
    await secureSave(STORAGE_KEY_COURSES, courses);
    await secureSave(STORAGE_KEY_SETTINGS, settings);
    if (shortlist !== undefined) {
      await secureSave(STORAGE_KEY_SHORTLIST, shortlist);
    }
  } catch (e) {
    console.warn('Failed to save to storage:', e);
  }
}

export async function loadFromLocalStorage(): Promise<{
  courses: Course[];
  settings: AppSettings;
  collegeShortlist?: ShortlistEntry[];
} | null> {
  try {
    const courses = await secureLoad<Course[]>(STORAGE_KEY_COURSES);
    const settings = await secureLoad<AppSettings>(STORAGE_KEY_SETTINGS);
    if (!courses || !settings) return null;
    const collegeShortlist = await secureLoad<ShortlistEntry[]>(STORAGE_KEY_SHORTLIST);
    return { courses, settings, collegeShortlist: collegeShortlist ?? [] };
  } catch (e) {
    console.warn('Failed to load from storage:', e);
    return null;
  }
}

export function exportSession(courses: Course[], settings: AppSettings): string {
  const data: SessionData = {
    version: SCHEMA_VERSION,
    courses,
    settings,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  return JSON.stringify(data, null, 2);
}

export function importSession(json: string): SessionData | null {
  try {
    const data = JSON.parse(json) as SessionData;
    if (!data.version || !data.courses || !data.settings) return null;
    if (data.version > SCHEMA_VERSION) {
      console.warn('Session file is from a newer version');
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

export async function clearLocalStorage(): Promise<void> {
  await secureRemove(STORAGE_KEY_COURSES);
  await secureRemove(STORAGE_KEY_SETTINGS);
  await secureRemove(STORAGE_KEY_SHORTLIST);
}
